import { MessageService, ToastMessageOptions } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { MAX_VISIBLE_TOASTS, toastDuration } from '../../enums/toast-life.enum';

/** Marks the prototypes once wrapped, so a second install is a no-op. */
const INSTALLED = Symbol.for('admin.toastReadingTime');

/** The parts of PrimeNG's p-toast the rules below lean on (primeng/toast 21). */
interface ToastInternals {
  messages: ToastMessageOptions[] | null | undefined;
  canAdd(message: ToastMessageOptions): boolean;
  add(messages: ToastMessageOptions[]): void;
  containsMessage(collection: ToastMessageOptions[] | null | undefined, message: ToastMessageOptions): boolean;
}

/**
 * A toast's message timed the same way as the app's (toastDuration): by its
 * words and tone, 6-10 s, a warning 8-12 s, an error 15 s. A life the screen
 * set may lengthen it, never past that. A sticky toast stays until closed and
 * is left alone.
 */
export function withReadingTime(message: ToastMessageOptions): ToastMessageOptions {
  if (!message || message.sticky) return message;
  const text = [message.summary, message.detail].filter(Boolean).join(' ');
  return { ...message, life: toastDuration(text, message.severity, message.life) };
}

/** Same severity, summary and detail - PrimeNG's own test for a duplicate. */
function sameToast(a: ToastMessageOptions, b: ToastMessageOptions): boolean {
  return a.severity === b.severity && a.summary === b.summary && a.detail === b.detail;
}

/**
 * The admin's toast rules (owner, 2026-10-25), the same as the app's:
 * - timed by reading time (withReadingTime);
 * - never the same message twice on screen - an outage used to stack one
 *   error per failed request;
 * - at most MAX_VISIBLE_TOASTS on screen; a new one replaces the oldest.
 *
 * Done on the classes, not by providing a subclass or setting an input on
 * every <p-toast>, because most admin screens provide their OWN
 * MessageService (`providers: [MessageService]`, beside their own p-toast) -
 * a root override would miss them, and a new screen would miss it again.
 * Every admin toast passes through MessageService.add/addAll and p-toast's
 * canAdd/add.
 */
export function installToastReadingTime(): void {
  const service = MessageService.prototype as MessageService & { [INSTALLED]?: boolean };
  if (service[INSTALLED]) return;
  const add = service.add;
  const addAll = service.addAll;
  service.add = function (this: MessageService, message: ToastMessageOptions) {
    add.call(this, withReadingTime(message));
  };
  service.addAll = function (this: MessageService, messages: ToastMessageOptions[]) {
    addAll.call(this, (messages ?? []).map(withReadingTime));
  };
  service[INSTALLED] = true;

  const toast = Toast.prototype as unknown as ToastInternals;
  const canAdd = toast.canAdd;
  const show = toast.add;
  toast.canAdd = function (this: ToastInternals, message: ToastMessageOptions) {
    return canAdd.call(this, message) && !this.containsMessage(this.messages, message);
  };
  toast.add = function (this: ToastInternals, messages: ToastMessageOptions[]) {
    // One addAll can carry the same message twice; canAdd only saw the screen.
    const fresh = messages.filter((m, i) => messages.findIndex((o) => sameToast(o, m)) === i);
    show.call(this, fresh);
    if (this.messages && this.messages.length > MAX_VISIBLE_TOASTS) {
      this.messages = this.messages.slice(-MAX_VISIBLE_TOASTS);
    }
  };
}
